import auth0 from 'auth0-js';
import store from '@/store/store';
import utils from '@/utils';
import {
  getShellSession,
  isRunningInShell,
  waitForShellSession
} from '@/auth/shellBridge';
import { resetExternalServiceErrorIdentity } from '@/analytics/reportExternalServiceError';

const AuthPlugin = {
  install(Vue) {
    Vue.prototype.$auth = new Vue({
      data: {
        authClient: null,
        clientId: '',
        roleName: '',
        timeoutDelay: 30 * 60 * 1000, //30分
        timeoutId: null
      },
      computed: {},
      methods: {
        init(roleName) {
          this.clientId = this.getClientId(roleName);
          this.roleName = roleName;
          if (this.clientId) {
            this.authClient = new auth0.WebAuth({
              audience: process.env.VUE_APP_AUTH0_URL + '/userinfo',
              clientID: this.clientId,
              domain: process.env.VUE_APP_AUTH0_DOMAIN,
              redirectUri: process.env.VUE_APP_CHAT_URL,
              responseType: 'token id_token',
              scope: 'openid profile'
            });
          }
        },
        login() {
          this.authClient.authorize();
        },
        getClientId(roleName) {
          if (utils.isCompany(roleName)) {
            return process.env.VUE_APP_AUTH0_COMPANY_CLIENT_ID;
          } else if (utils.isInfluencer(roleName)) {
            return process.env.VUE_APP_AUTH0_INFLUENCER_CLIENT_ID;
          }
        },
        checkSession() {
          // ユーザーのログイン状態を問い合わせる
          return new Promise((resolve, reject) => {
            const useShellSession = shellSession => {
              this.setUserStatus(shellSession);
              this.scheduleCheckSession();
              resolve(shellSession);
            };

            // Inside the shell, use its tokens instead of asking Auth0
            const shellSession = getShellSession();

            if (shellSession) {
              useShellSession(shellSession);
              return;
            }

            // An expired one means the shell is already fetching the next: wait for it
            if (isRunningInShell()) {
              waitForShellSession().then(freshSession => {
                if (freshSession) {
                  useShellSession(freshSession);
                  return;
                }

                // Asking Auth0 always fails inside the shell and would sign
                // Firebase out. Keep the current Firebase session and check
                // again later, once the shell has a session to hand over.
                this.scheduleCheckSession();
                reject(new Error('The app shell has no valid session yet'));
              });
              return;
            }

            this.askAuth0(resolve, reject);
          });
        },
        askAuth0(resolve, reject) {
          if (!this.authClient) {
            reject();
            return;
          }
          this.authClient.checkSession({}, (error, response) => {
            if (response && response.idToken) {
              this.setUserStatus(response);
              this.scheduleCheckSession();
              resolve(response);
            } else if (error) {
              this.logout();
              reject(error);
            }
          });
        },
        logout() {
          clearTimeout(this.timeoutId);
          resetExternalServiceErrorIdentity();

          // Navigating away here would clear the sessions the other tabs use
          if (!isRunningInShell()) {
            this.authClient.logout({
              returnTo: utils.getBackUrl(this.roleName)
            });
          }

          this.$firebase.signOut();
        },
        setUserStatus(response) {
          store.dispatch('setUser', {
            idToken: response.idToken,
            name: response.idTokenPayload.name,
            picture: response.idTokenPayload.picture,
            sub: response.idTokenPayload.sub
          });
        },
        scheduleCheckSession() {
          clearTimeout(this.timeoutId);
          // 定期的にログイン状態をチェックする
          this.timeoutId = setTimeout(this.checkSession, this.timeoutDelay);
        }
      }
    });
  }
};

export default AuthPlugin;
